import { ApiResponseType, jsonInit, toSearchParams } from '@shigo-ui/api-client';
import { z } from 'zod';

import { createShigoApiClient, type ShigoApiConfig } from '../../../lib/shigo-api-client';
import { statusCountsSchema } from '../../../lib/statusCounts';
import type { PublisherDomainUpdatePayload } from './publisherDomainForm';
import {
  domainComplianceApprovalsSchema,
  domainPublisherPageSchema,
  domainsPageSchema,
  publisherDomainsPageSchema,
  type DomainComplianceApproval,
  type DomainComplianceMutationPayload,
  type DomainPublisherOption,
  type DomainsFilter,
  type PublisherDomainCreatePayload,
  type PublisherDomainsFilter,
} from './model';

interface DomainsClientOptions {
  config: ShigoApiConfig;
  accessToken: string;
  fetcher?: typeof fetch;
}

const mutationResponseSchema = z.preprocess(
  (value) => (value === '' || value === undefined ? null : value),
  z.union([z.null(), z.looseObject({}), z.array(z.looseObject({}))]),
);

export function createDomainsClient({ config, accessToken, fetcher }: DomainsClientOptions) {
  const api = createShigoApiClient(config, accessToken, fetcher);

  return {
    setPublisherDomainActive: (publisherId: string, domainId: string, active: boolean) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/domains/${encodeURIComponent(domainId)}/${active ? 'unarchive' : 'archive'}`,
        schema: mutationResponseSchema,
        init: jsonInit('PUT', {}),
      }),
    updatePublisherDomain: (
      publisherId: string,
      domainId: string,
      payload: PublisherDomainUpdatePayload,
    ) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/domains/${encodeURIComponent(domainId)}`,
        schema: mutationResponseSchema,
        init: jsonInit('PUT', payload),
      }),
    getPublisherStatusCounts: (publisherId: string, searchTerm: string) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/domains/statuses?${toSearchParams({ searchTerm: searchTerm.trim() })}`,
        schema: statusCountsSchema,
      }),
    list: (filter: DomainsFilter) =>
      api.request({
        path: `/api/dps/domain-compliances?${serializeDomainsFilter(filter)}`,
        schema: domainsPageSchema,
      }),
    listPublisherDomains: (publisherId: string, filter: PublisherDomainsFilter) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/domains?${serializePublisherDomainsFilter(filter)}`,
        schema: publisherDomainsPageSchema,
      }),
    listApprovals: async (domain: string): Promise<DomainComplianceApproval[]> => {
      const approvals = await api.request({
        path: `/api/dps/domain-approvals/${encodeURIComponent(domain)}`,
        schema: domainComplianceApprovalsSchema,
      });
      return approvals.filter((approval) => approval.demandPartner.status === 'ACTIVE');
    },
    updateApprovals: (payload: DomainComplianceMutationPayload) =>
      api.request({
        path: '/api/dps/domain-approvals/bulk',
        schema: mutationResponseSchema,
        init: jsonInit('PUT', payload),
      }),
    listPublishers: () => listAllPublishers(api),
    createPublisherDomain: (publisherId: string, payload: PublisherDomainCreatePayload) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/domains`,
        schema: mutationResponseSchema,
        init: jsonInit('POST', payload),
      }),
    importPublisherDomains: (publisherId: string, file: File) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/domains/csv/import`,
        schema: mutationResponseSchema,
        init: fileUploadInit('POST', file),
      }),
    exportPublisherDomains: (publisherId: string) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/domains/csv/export`,
        schema: z.string(),
        responseType: ApiResponseType.Text,
      }),
    reimportPublisherDomains: (publisherId: string, file: File) =>
      api.request({
        path: `/api/publishers/${encodeURIComponent(publisherId)}/domains/csv/reimport`,
        schema: mutationResponseSchema,
        init: fileUploadInit('PUT', file),
      }),
  };
}

function fileUploadInit(method: 'POST' | 'PUT', file: File) {
  const body = new FormData();
  body.append('file', file, file.name);
  return { method, body };
}

export function serializeDomainsFilter(filter: DomainsFilter) {
  return toSearchParams({
    page: filter.page,
    size: filter.size,
    ...(filter.sortProperty
      ? { sortProperty: filter.sortProperty, sortDirection: filter.sortDirection }
      : {}),
    publisherIds: filter.publisherIds.length ? filter.publisherIds : undefined,
    url: filter.searchTerm.trim() || undefined,
  }).toString();
}

export function serializePublisherDomainsFilter(filter: PublisherDomainsFilter) {
  return toSearchParams({
    page: filter.page,
    size: filter.size,
    searchTerm: filter.searchTerm.trim() || undefined,
    statuses: filter.statuses,
  }).toString();
}

async function listAllPublishers(
  api: ReturnType<typeof createShigoApiClient>,
): Promise<DomainPublisherOption[]> {
  const pageSize = 100;
  const maxPages = 100;
  const publishers: DomainPublisherOption[] = [];
  for (let page = 0; page < maxPages; page += 1) {
    const response = await api.request({
      path: `/api/publishers?${toSearchParams({
        page,
        size: pageSize,
        status: 'ACTIVE',
        sortProperty: 'name',
        sortDirection: 'ASC',
      })}`,
      schema: domainPublisherPageSchema,
    });
    publishers.push(...response.content);
    if (response.content.length < pageSize || publishers.length >= response.totalElements) {
      return publishers;
    }
  }
  return publishers;
}

export type DomainsClient = ReturnType<typeof createDomainsClient>;
